"use client";

import { c as _c } from "react/compiler-runtime";
import { createContext, type RefObject, useContext } from "react";

// CSS variable references for pie chart theming
export const pieCssVars = {
  background: "var(--chart-background)",
  foreground: "var(--chart-foreground)",
  foregroundMuted: "var(--chart-foreground-muted)",
  label: "var(--chart-label)",
  // Default slice colors from chart palette
  slice1: "var(--chart-1)",
  slice2: "var(--chart-2)",
  slice3: "var(--chart-3)",
  slice4: "var(--chart-4)",
  slice5: "var(--chart-5)"
};

// Default slice color palette
export const defaultPieColors = [pieCssVars.slice1, pieCssVars.slice2, pieCssVars.slice3, pieCssVars.slice4, pieCssVars.slice5];
export interface PieData {
  /** Display label for the slice */
  label: string;
  /** Value for the slice (determines slice size relative to total) */
  value: number;
  /** Optional logo for hover/center renderers */
  logo?: string;
  /** Optional color override - falls back to palette */
  color?: string;
  /** Optional fill override for patterns/gradients (e.g., "url(#patternId)") */
  fill?: string;
}

/** Arc data computed by visx Pie */
export interface PieArcData {
  data: PieData;
  index: number;
  startAngle: number;
  endAngle: number;
  padAngle: number;
  value: number;
}
export interface PieContextValue {
  // Data
  data: PieData[];
  arcs: PieArcData[];

  // Dimensions
  size: number;
  center: number;
  outerRadius: number;
  innerRadius: number;
  padAngle: number;
  cornerRadius: number;

  // Hover effect
  hoverOffset: number;

  // Hover state
  hoveredIndex: number | null;
  setHoveredIndex: (index: number | null) => void;

  // Animation state
  animationKey: number;
  isLoaded: boolean;

  // Container ref for portals
  containerRef: RefObject<HTMLDivElement | null>;

  // Computed values
  totalValue: number;

  // Get color for a slice index
  getColor: (index: number) => string;

  // Get fill for a slice index (supports patterns/gradients)
  getFill: (index: number) => string;
}
const PieContext = createContext<PieContextValue | null>(null);
export function PieProvider(t0) {
  const $ = _c(3);
  const {
    children,
    value
  } = t0;
  let t1;
  if ($[0] !== children || $[1] !== value) {
    t1 = <PieContext.Provider value={value}>{children}</PieContext.Provider>;
    $[0] = children;
    $[1] = value;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
}
export function usePie() {
  const context = useContext(PieContext);
  if (!context) {
    throw new Error("usePie must be used within a PieProvider. Make sure your component is wrapped in <PieChart>.");
  }
  return context;
}
export default PieContext;